import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import PropTypes from "prop-types";
import ImmutablePropTypes from "react-immutable-proptypes";
import classnames from "classnames";
import { Client } from "resourceModels";
import { getResource } from "selectors/resources";
import tinycolor from "tinycolor2";
import ModalSideBar from "components/Common/ModalSideBar";
import Button from "components/Common/Button";
import Pill from "components/Common/Pill";
import InfoTooltip from "components/Common/InfoTooltip";
import InputClearable from "components/Common/InputClearable";
import SvgIcon from "components/Common/SvgIcon";
import SVG from "react-inlinesvg";
import colors from "stylesheets/utilities/colors.scss";
import {
  getBotTopicsAction, addBotTopicAction, removeBotTopicAction,
  updateQueryAction
} from "./actions";
import "./style.scss";

/** Creates the bot routing config modal */
export class ModalBotRoutingConfig extends Component {
  /**
  * Creates the bot lists in the bot tab.
  * @param {Immutable.Map} bot a map containing all the information about the bot
  * @returns {ReactElement}
  */
  static createBot(bot) {
    const avatar = bot.get("avatar");

    return (
      <div className="BotRoutingConfig__bots__bot" key="bot">
        <div
          style={{ backgroundColor: bot.get("tint") || colors.colorBlack }}
          className="BotRoutingConfig__bots__bot__background"
        >
          {
            avatar.get("useLetter") ? (
              <div className={classnames("BotRoutingConfig__bots__bot__background__text",
                tinycolor(bot.get("tint")).isLight()
                  ? "BotRoutingConfig__letter--dark" : "BotRoutingConfig__letter--light")}
              >
                {avatar.get("letter")}
              </div>
            ) : (
              <SVG
                src={avatar.get("image")}
                className="BotRoutingConfig__bots__bot__background__icon"
              />
            )
          }
        </div>
        <div className="BotRoutingConfig__bots__bot__text">
          {bot.get("name")}
        </div>
      </div>
    );
  }

  /**
   * component constructor
   * @param {Object} props properties passed to component
   */
  constructor(props) {
    super(props);
    this.updateTopicSearch = this.updateTopicSearch.bind(this);
    this.addTopic = this.addTopic.bind(this);
    this.sidebarOnClick = this.sidebarOnClick.bind(this);

    /**
     * @constant
     * @type {Array}
     */
    this.BUTTONS = [{
      name: "topics",
      icon: "Topic",
      text: "Topics"
    },
    {
      name: "bots",
      icon: "BotRoutingLight",
      text: "Bots"
    }];
    this.state = {
      active: "topics"
    };
  }

  /**
  * retreives the bot topics and client information.
  */
  componentDidMount() {
    const { getBotTopics } = this.props;
    getBotTopics();
  }

  /**
  * Sets which tab is active when a tab button is clicked.
  * @param {Object} e on click event
  */
  sidebarOnClick(e) {
    this.setState({ active: e.target.title });
  }

  /**
  * adds a new topic to the bot also catches duplicates
  * @param {String} event
  */
  addTopic(event) {
    const { modalBotRoutingConfig, addBotTopic, updateQuery } = this.props;
    const query = modalBotRoutingConfig.get("query");
    const topics = modalBotRoutingConfig.get("botTopics");

    if (((event.key === "Enter" && query !== "") || !event.key) && query !== "") {
      const hasDuplicate = Boolean(topics.find(topic => topic.topic === query));

      if (!hasDuplicate) {
        addBotTopic(query);
      } else {
        updateQuery(query, hasDuplicate);
      }
    }
  }

  /**
  * updates the input bar to match the users input
  * @param {Object} event the id of the topic to be removed
  */
  updateTopicSearch(event) {
    const { updateQuery } = this.props;

    if (event.key === "Enter") {
      this.addTopic();
    }

    updateQuery(event.target.value);
  }

  /**
  * removes a bot topic
  * @param {String} id the id of the topic to be removed
  */
  removeTopic(id) {
    const { removeBotTopic } = this.props;
    removeBotTopic(id);
  }

  /**
  * generates the pills in the topics tab, displayes a message if there are no pills.
  * @param {Immutable.List} topics a list of immutable records.
  * Each record is a topic covered by the bot.
  * @param {String} type what type of list is to be generated "pills" vs "text"
  * @returns {ReactElement|String}
  */
  generatePillSection(topics, type) {
    const {
      /** @type Client */
      client
    } = this.props;

    const colour = client.tint || "";

    switch (type) {
      case "pills":
        return (
          <div className="BotRoutingConfig__topics__pills">
            {topics.map(topic => (
              <Pill
                key={topic.id}
                id={topic.id}
                description={topic.topic}
                pillColor={colour}
                icon="NoEntry"
                buttonOnClick={() => this.removeTopic(topic.id)}
              />
            ))}
          </div>
        );
      case "text":
        return (
          <div className="BotRoutingConfig__topics__placeholder">
            For example: mobile phone plans, internet, wifi, etc.
          </div>
        );
      default:
        return ("Something went wrong. Failed to generate pill section.");
    }
  }

  /**
  * generates the body section of the modal (topics and bots)
  * @returns {ReactElement}
  */
  generateBody() {
    const {
      modalBotRoutingConfig,
      /** @type Client */
      client
    } = this.props;
    const { active } = this.state;
    const query = modalBotRoutingConfig.get("query");
    const topics = modalBotRoutingConfig.get("botTopics");
    const err = modalBotRoutingConfig.get("errorMessage");
    const botList = client.routingInfo;
    const isBotRoutingEnabled = botList.getIn([client.token, "botRouting"]);

    const connectedBots = botList
      .filter((botInfo, botToken) => (
        (botToken !== client.token &&
        botInfo.get("botRouting") === true &&
        isBotRoutingEnabled)
      ));

    const disconnectedBots = botList
      .filter((botInfo, botToken) => (
        (botToken !== client.token &&
        (botInfo.get("botRouting") === false || !isBotRoutingEnabled))
      ));

    switch (active) {
      /** generates the topics tab of the modal */
      case "topics":
        return (
          <div className="BotRoutingConfig__body">
            <h6 className="BotRoutingConfig__topics__title">
              <div className="BotRoutingConfig__topics__title__text">
                Topics {client.name} Supports
              </div>
              <div className="BotRoutingConfig__topics__title__tooltip">
                <InfoTooltip
                  blurb="Topics inform other bots what this bot can help with."
                  iconDefault="QuestionCircleFilled"
                  inModal
                  absolute
                  customPosition={{
                    dimple: "up",
                    top: 36,
                    left: -82
                  }}
                />
              </div>
            </h6>
            <div className="BotRoutingConfig__topics">
              <div className="BotRoutingConfig__topics__add">
                <InputClearable
                  customClassName="ModalResponseDetails__tag-container__search-container__search"
                  value={query}
                  onChange={this.updateTopicSearch}
                  onKeyPress={this.addTopic}
                  placeholder="Type a general topic your bot can support"
                />
                <Button
                  id="addTopic"
                  onClick={this.addTopic}
                  customClassName="ModalResponseDetails__tag-container__search-container__button"
                  title="Add a new Topic"
                  icon="CircleAddAlternate"
                  fillColor={query.length ? colors.colorUIPrimary : colors.colorGrey4}
                  clear
                />
              </div>
              {err !== "" && this.renderErrorMessage()}
              {this.generatePillSection(topics, topics.size ? "pills" : "text")}
            </div>
          </div>
        );
      /** generates the bots tab of the modal */
      case "bots":
        return (
          <div className="BotRoutingConfig__body">
            <h6 className="BotRoutingConfig__bots__title">
              <div className="BotRoutingConfig__bots__title__text">
                Connected to {client.name}
              </div>
              <div className="BotRoutingConfig__bots__title__toolTip">
                <InfoTooltip
                  blurb="Connected bots can switch to and from one another."
                  iconDefault="QuestionCircleFilled"
                  customPosition={{
                    dimple: "up",
                    top: 36,
                    left: -82
                  }}
                />
              </div>
            </h6>
            {
              connectedBots.toList().map(bot => ModalBotRoutingConfig.createBot(bot))

            }
            {disconnectedBots.size > 0 && (
              <h6 className="BotRoutingConfig__bots__sub-title">
                Not Connected
              </h6>
            )}
            {
              disconnectedBots.toList().map(bot => ModalBotRoutingConfig.createBot(bot))
            }
            <div className="BotRoutingConfig__bots__blurb">
              Contact Ada to add or remove bot connections
            </div>

          </div>
        );
      default:
        return <div>Something went wrong</div>;
    }
  }

  /**
  * creates the error message for the topics tab
  * @returns {ReactElement}
  */
  renderErrorMessage() {
    const { modalBotRoutingConfig } = this.props;
    const err = modalBotRoutingConfig.get("errorMessage");

    return (
      <div className="BotRoutingConfig__topics__error">
        <div className="BotRoutingConfig__topics__error__icon">
          <SvgIcon
            icon="Warning"
            fillColor={colors.colorUIWarnActive}
            height={24}
          />
        </div>
        <div className="BotRoutingConfig__topics__error__text">
          {err}
        </div>
      </div>
    );
  }

  /**
   * renders the component
   * @returns {React.Fragment}
   */
  render() {
    const { active } = this.state;

    return (
      <div className="Modal__modal">
        <h5 className="Modal__title">Configure Bot Switch</h5>
        <div className="Modal__body">
          <ModalSideBar
            buttons={this.BUTTONS}
            onClick={this.sidebarOnClick}
            activeButton={active}
          />
          <div className="Modal__body__container">
            {this.generateBody()}
          </div>
        </div>
      </div>
    );
  }
}

ModalBotRoutingConfig.propTypes = {
  /** gets all the topics for the bot */
  getBotTopics: PropTypes.func.isRequired,
  /** adds a topic to the bot */
  addBotTopic: PropTypes.func.isRequired,
  /** removes a topic form the bot */
  removeBotTopic: PropTypes.func.isRequired,
  /** a function that updates the users topic query and error messages */
  updateQuery: PropTypes.func.isRequired,
  /** modal information */
  modalBotRoutingConfig: ImmutablePropTypes.map.isRequired,

  client: PropTypes.instanceOf(Client).isRequired
};

/**
 * pulls information from upper states
 * @param {Object} state
 * @returns {Object}
 */
function mapState(state) {
  const { modalBotRoutingConfig } = state;

  return {
    client: getResource(state, "client"),
    modalBotRoutingConfig
  };
}

/**
 * binds action creators
 * @param {Function} dispatch
 * @returns {Object}
 */
function mapDispatch(dispatch) {
  return {
    getBotTopics: bindActionCreators(getBotTopicsAction, dispatch),
    addBotTopic: bindActionCreators(addBotTopicAction, dispatch),
    removeBotTopic: bindActionCreators(removeBotTopicAction, dispatch),
    updateQuery: bindActionCreators(updateQueryAction, dispatch)
  };
}

const Connector = connect(mapState, mapDispatch)(ModalBotRoutingConfig);

export default Connector;
